/**
 * What an order row's QUANTITY column says.
 *
 * Pure and React-free: "how much of this actually traded" is the number a
 * trader acts on, and the case that matters — a partial fill whose
 * remainder was refused — is exactly the one that read backwards.
 */

/** Only what this needs, so the store's shape is not a dependency. */
export interface QtyLeg {
  readonly filledQty: number
  readonly qty?: number | null
  readonly lots: number
}

/**
 * The size to print, in UNITS.
 *
 * `filledQty` once anything has filled, because that is what actually
 * traded; otherwise the ordered quantity. Falls back to lots with an `L`
 * only when the engine sent no `qty` — an older event, or a leg whose lot
 * size was unknown — because a bare number that might be lots or might be
 * units is worse than an explicitly-labelled one.
 */
export function qtyOf(leg: QtyLeg): string {
  const ordered = typeof leg.qty === 'number' && leg.qty > 0 ? leg.qty : null

  /*
   * ── A PARTIAL FILL SHOWS BOTH NUMBERS — 9.38 ────────────────────────────
   *
   * This printed `filledQty` alone whenever anything had filled. Beside a
   * "Rejected" pill that reads as the opposite of what happened: a 20,000
   * SENSEX sell that filled 8,000 and had the rest refused for margin was
   * drawn as "8000 — Rejected", which says eight thousand were rejected.
   * Eight thousand were SOLD, and the trader is short them.
   *
   * Both numbers, always, when they differ. The pill still says what became
   * of the ORDER; this column says what became of the quantity, and the two
   * together are the whole truth of the row.
   */
  if (leg.filledQty > 0 && ordered !== null && leg.filledQty < ordered) {
    return `${leg.filledQty} / ${ordered}`
  }
  if (leg.filledQty > 0) return String(leg.filledQty)
  if (ordered !== null) return String(ordered)
  return `${leg.lots}L`
}

/**
 * What a row's quantity means, said in full on hover.
 *
 * Only when part of it traded, because that is the only case the two
 * numbers in the cell do not already explain by themselves.
 */
export function qtyTitle(leg: QtyLeg): string | undefined {
  const ordered = typeof leg.qty === 'number' && leg.qty > 0 ? leg.qty : null
  if (leg.filledQty <= 0 || ordered === null || leg.filledQty >= ordered) return undefined
  return (
    `${leg.filledQty} of ${ordered} filled. The remaining ${ordered - leg.filledQty} did not — ` +
    'the status beside it says why, and the position you hold is the filled part.'
  )
}
